// src/api/forms.js

import apiClient from './client';

const formsApi = {
  // Get form categories with template and submission counts
  getFormCategories: async () => {
    try {
      const response = await apiClient.get('/forms/categories');
      return response.data.categories || response.data;
    } catch (error) {
      console.error('Error fetching form categories:', error);
      // Return fallback data structure based on typical medical practice forms
      return [
        { 
          id: 'consent', 
          name: 'Consent Forms', 
          icon: 'consent',
          templates: 0, 
          submissions: 0,
          color: 'secondary',
          description: 'Treatment consent and authorization forms'
        },
        { 
          id: 'medical', 
          name: 'Medical History', 
          icon: 'medical',
          templates: 0, 
          submissions: 0,
          color: 'error',
          description: 'Patient medical history and health questionnaires'
        },
        { 
          id: 'treatment', 
          name: 'Treatment Forms', 
          icon: 'treatment',
          templates: 0, 
          submissions: 0,
          color: 'info',
          description: 'Treatment plans and procedure forms'
        },
        { 
          id: 'assessment', 
          name: 'Assessment Forms', 
          icon: 'assessment',
          templates: 0, 
          submissions: 0,
          color: 'warning',
          description: 'Patient assessments and evaluations'
        }
      ];
    }
  },

  // Get recent form submissions across all forms
  getRecentSubmissions: async (limit = 10) => {
    try {
      const response = await apiClient.get('/forms/recent-submissions', {
        params: { limit }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching recent form submissions:', error);
      return [];
    }
  },

  // Get all forms for a specific category
  getFormsByCategory: async (categoryId, params = {}) => {
    try {
      const response = await apiClient.get(`/forms/category/${categoryId}`, {
        params: {
          skip: params.skip || 0,
          limit: params.limit || 50,
          order_by: params.order_by || 'created_date',
          order_direction: params.order_direction || 'desc',
          ...params
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching forms for category ${categoryId}:`, error);
      throw error;
    }
  },

  // Get all form submissions with filtering options
  getAllSubmissions: async (params = {}) => {
    try {
      const response = await apiClient.get('/forms/submissions', {
        params: {
          page: params.page || 1,
          limit: params.limit || 50,
          order_by: params.order_by || 'submitted_date',
          order_direction: params.order_direction || 'desc',
          category: params.category,
          status: params.status,
          client_id: params.client_id,
          search: params.search,
          date_from: params.date_from,
          date_to: params.date_to
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching form submissions:', error);
      throw error;
    }
  },

  // Get a specific form template
  getFormTemplate: async (formId) => {
    try {
      const response = await apiClient.get(`/forms/template/${formId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching form template ${formId}:`, error);
      throw error;
    }
  },

  // Get a specific form submission
  getFormSubmission: async (submissionId) => {
    try {
      const response = await apiClient.get(`/forms/submission/${submissionId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching form submission ${submissionId}:`, error);
      throw error;
    }
  },

  // Create a new form template
  createFormTemplate: async (formData) => {
    try {
      const response = await apiClient.post('/forms/template', formData);
      return response.data;
    } catch (error) {
      console.error('Error creating form template:', error);
      throw error;
    }
  },

  // Update an existing form template
  updateFormTemplate: async (formId, formData) => {
    try {
      const response = await apiClient.put(`/forms/template/${formId}`, formData);
      return response.data;
    } catch (error) {
      console.error(`Error updating form template ${formId}:`, error);
      throw error;
    }
  },

  // Delete a form template
  deleteFormTemplate: async (formId) => {
    try {
      await apiClient.delete(`/forms/template/${formId}`);
      return true;
    } catch (error) {
      console.error(`Error deleting form template ${formId}:`, error);
      throw error;
    }
  },

  // Search forms and submissions
  searchForms: async (query, options = {}) => {
    try {
      const response = await apiClient.get('/forms/search', {
        params: {
          q: query,
          type: options.type || 'both', // 'templates', 'submissions', or 'both'
          category: options.category,
          limit: options.limit || 20,
          ...options
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching forms:', error);
      throw error;
    }
  },

  // Get form statistics for dashboard/analytics
  getFormStatistics: async (dateRange = '30d') => {
    try {
      const response = await apiClient.get('/forms/statistics', {
        params: { period: dateRange }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching form statistics:', error);
      return {
        totalTemplates: 0,
        totalSubmissions: 0,
        submissionsThisPeriod: 0,
        averageCompletionRate: 0,
        popularForms: [],
        categoryBreakdown: []
      };
    }
  }
};

export default formsApi;